<template>
  <mc-split-view
    container-class="criteria"
    :has-right-header="!!selectedCriteria"
    height="500px"
  >
    <template #leftHeader>
      <div class="header-title">
        Criteria
      </div>
    </template>
    <template #leftBody>
      <mc-dnd-list
        v-if="value.length > 0"
        container-class="criteria__list"
        :list="internalCriteria"
        :on-drop="moveCriteriaHandler"
      >
        <template #item="{ item, index }">
          <div
            :class="[
              'criteria__item',
              { 'criteria__item--active': selectedIndex === index }
            ]"
            @click="selectedIndex = index"
          >
            <div class="criteria__title">
              {{ item.key }}
            </div>
            <a
              class="criteria__action"
              role="button"
              @click.stop="deleteCriteriaHandler(index)"
            >
              <b-icon icon="delete-outline" type="is-dark" />
            </a>
            <b-icon icon="chevron-right" />
          </div>
        </template>
      </mc-dnd-list>
      <div v-else class="criteria__info">
        <b-icon icon="information" />
        <div class="content">
          There no criteria defined for this challenge
        </div>
      </div>
      <form
        class="criteria__add-criterion"
        @submit.prevent="addCriteriaHandler"
      >
        <b-field v-if="isAddingCriterion">
          <b-input
            v-model="newCriterionKey"
            v-bfocus="true"
            expanded
            placeholder="Criterion Key"
            type="text"
            icon="key"
          />
          <p class="control">
            <button class="button" native-type="submit">
              <b-icon icon="check" size="is-small" />
            </button>
          </p>
          <p class="control">
            <button class="button" @click="isAddingCriterion = false">
              <b-icon icon="close" size="is-small" />
            </button>
          </p>
        </b-field>
        <b-button
          v-else
          class="is-fullwidth"
          type="is-light"
          @click="isAddingCriterion = true"
        >
          Add Criteria
        </b-button>
      </form>
    </template>
    <template v-if="selectedCriteria" #rightHeader>
      <form class="header-title" @submit.prevent="editKeyHandler">
        <b-field v-if="isEditingKey">
          <b-input
            v-model="editingCriterionKey"
            v-bfocus="true"
            placeholder="Criterion Key"
            type="text"
            icon="key"
            size="is-small"
          />
          <p class="control">
            <button class="button is-small" native-type="submit">
              <b-icon icon="check" size="is-small" />
            </button>
          </p>
          <p class="control">
            <button class="button is-small" @click="isEditingKey = false">
              <b-icon icon="close" size="is-small" />
            </button>
          </p>
        </b-field>
        <fragment v-else>
          <span>{{ selectedCriteria.key }}</span>
          <a class="editor__edit-key" @click="openKeyEditor">
            <b-icon icon="pencil" size="is-small" />
          </a>
        </fragment>
      </form>
    </template>
    <template #rightBody>
      <div v-if="selectedCriteria" class="criteria__editor">
        <b-field label="Description">
          <mc-markdown-editor
            :value="selectedCriteria.description"
            @input="
              fieldChangeHandler({
                target: { name: 'description', value: $event }
              })
            "
          />
        </b-field>
        <b-field class="criteria__justify">
          <b-switch
            :value="selectedCriteria.justify"
            @input="
              fieldChangeHandler({
                target: { name: 'justify', value: $event }
              })
            "
          >
            <b>Justify</b>
          </b-switch>
        </b-field>
        <b-field label="Weightage">
          <b-input
            name="weight"
            type="number"
            :value="selectedCriteria.weight"
            @input.native="fieldChangeHandler"
          />
        </b-field>
      </div>
      <div v-else class="criteria__info">
        <b-icon icon="information" />
        <div class="content">
          Select a criteria to edit!
        </div>
      </div>
    </template>
  </mc-split-view>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';

import regex from '../../utils/regex';
import { removeByIndex, moveByElement } from '../../utils/array';

export default {
  props: {
    value: {
      type: Array,
      default: function() {
        return [];
      }
    }
  },
  data: function() {
    return {
      editingCriterionKey: '',
      internalCriteria: [],
      isAddingCriterion: false,
      isEditingKey: false,
      newCriterionKey: '',
      selectedIndex: null
    };
  },
  computed: {
    selectedCriteria: function() {
      if (this.selectedIndex === null) return null;
      return this.internalCriteria[this.selectedIndex];
    }
  },
  watch: {
    value: {
      handler: function() {
        this.internalCriteria = cloneDeep(this.value);
      },
      immediate: true
    }
  },
  methods: {
    addCriteriaHandler() {
      const key = this.newCriterionKey;
      if (!this.isKeyValid(key)) {
        this.$buefy.toast.open({
          message: regex.criterionKey.message,
          type: 'is-danger'
        });
      } else if (this.doesKeyExist(key)) {
        this.$buefy.toast.open({
          message: 'A criterion with same key already exists!',
          type: 'is-danger'
        });
      } else {
        this.internalCriteria.push({
          key,
          description: '',
          weight: 0,
          justify: false
        });
        this.newCriterionKey = '';
        this.isAddingCriterion = false;

        this.triggerOnChange();
      }
    },
    deleteCriteriaHandler(index) {
      removeByIndex(this.internalCriteria, index);
      this.triggerOnChange();
    },
    doesKeyExist(key) {
      return this.internalCriteria.find(o => o.key === key);
    },
    editKeyHandler() {
      const oldKey = this.selectedCriteria.key;
      const newKey = this.editingCriterionKey;

      if (oldKey === newKey) {
        this.isEditingKey = false;
        return;
      }

      if (!this.isKeyValid(newKey)) {
        this.$buefy.toast.open({
          message: regex.criterionKey.message,
          type: 'is-danger'
        });
      } else if (this.doesKeyExist(newKey)) {
        this.$buefy.toast.open({
          message: 'A criterion with same key already exists!',
          type: 'is-danger'
        });
      } else {
        this.selectedCriteria.key = newKey;
        this.isEditingKey = false;
        this.triggerOnChange();
      }
    },
    fieldChangeHandler({ target }) {
      if (target.type === 'number') {
        this.selectedCriteria[target.name] = parseInt(target.value, 10);
      } else {
        this.selectedCriteria[target.name] = target.value;
      }
      this.triggerOnChange();
    },
    isKeyValid(key) {
      return regex.criterionKey.pattern.test(key);
    },
    moveCriteriaHandler({ fromItem, toItem }) {
      // Maintaining selected criteria across moves
      const selectedKey = this.selectedCriteria && this.selectedCriteria.key;
      moveByElement(this.internalCriteria, fromItem, toItem);
      if (selectedKey) {
        this.selectedIndex = this.internalCriteria.findIndex(
          o => o.key === selectedKey
        );
      }

      this.triggerOnChange();
    },
    openKeyEditor() {
      this.editingCriterionKey = this.selectedCriteria.key;
      this.isEditingKey = true;
    },
    triggerOnChange() {
      this.$emit('input', cloneDeep(this.internalCriteria));
    }
  }
};
</script>

<style lang="scss" scoped>
.criteria {
  background-color: #ffffff;
  margin-bottom: 1.5rem;

  &__list {
    display: flex;
    flex: 1 0 0;
    flex-direction: column;
    overflow-y: auto;

    & /deep/ .dnd {
      &__draggable {
        box-shadow: none;
        padding: 0;
        border-bottom: 1px solid $border-color;

        &:not(:last-child) {
          margin-bottom: 0;
        }
      }

      &__draggable-handle {
        margin-right: 0;
        margin: 0 0.5rem;
      }
    }
  }

  &__item {
    display: flex;
    flex-direction: row;
    align-items: center;
    cursor: pointer;
    padding: 0.5rem;

    &:hover {
      background-color: rgba($secondary-color, 0.5);
    }

    &--active {
      background-color: $secondary-color !important;
    }
  }

  &__title {
    flex: 1 0 0;
  }

  &__action {
    margin-left: 0.5rem;
  }

  &__info {
    flex: 1 0 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 0.5rem;

    .content {
      margin-left: 0.5rem;
    }
  }

  &__add-criterion {
    padding: 0.5rem;
  }

  &__editor {
    display: flex;
    flex-direction: column;
    padding: 0.5rem;
  }

  &__justify {
    display: flex;
    flex-direction: column;
  }
}
</style>
